/**
 * Client Request — the module's API contract, in one place so the list and the
 * detail page cannot drift apart.
 *
 * Calls go through the ONE shared axios client (`api/client.ts`), which attaches
 * the JWT + X-Division-Id, drives the global loader, and arms the audit reason
 * header.  Never introduce a second axios instance (§14b.1).
 */
import { api } from '../../api/client';

/** dbo.AuditData.FormName for every audit row this module writes.  Registered
 *  in CLAUDE.md §14b.2 — one name, so the audit log and the reason gate resolve
 *  the same form. */
export const AUDIT_FORM_NAME = 'Client Request';

/** One Priority the form offers — a row of Settings → Utility → Client
 *  Request → Priority. The request saves `id`; `label` is what it shows. */
export interface PriorityOption { id: string; label: string }

/** The Priority a new request starts on — the lab's ordinary commitment. Matched
 *  by NAME against the Priority table, so it is skipped if that row is renamed
 *  or removed rather than pointing at something that no longer exists. */
export const DEFAULT_PRIORITY_NAME = 'Next Day';

/** "How did you hear about us" options (prototype v46).  The API is
 *  authoritative, this is only the pre-arrival
 *  fallback so the dropdown is never empty on first paint. */
export const HEARD_FALLBACK = [
  'Google', 'Remediation company referral', 'Repeat client',
  'Referred by another client', 'Contractor / consultant', 'Other',
];

/** One row in the Request Registration grid. */
export interface ClientRequestRow {
  id:           string;
  requestNo:    string | null;
  clientId:     string;
  clientName:   string | null;
  contactId:    string | null;
  contactName:  string | null;
  projectId:    string | null;
  projectName:  string | null;
  /** The client-facing project code, not the Oid. */
  projectCode:  string | null;
  /** The chosen Priority row's Oid; `priority` is its CURRENT name. */
  priorityId:   string | null;
  priority:     string | null;
  quoteNo:      string | null;
  status:       string | null;
  ownerId:      string | null;
  ownerName:    string | null;
  createdAt:    string;
  modifiedAt:   string | null;

  // ── Aggregated from the request's Service Tasks ──────────────────────────
  /** DISTINCT category CODES, comma-separated (e.g. "SUR,SAM"). Codes, not
   *  labels — `ServiceCategory` maps them to chip text + tone, so the
   *  vocabulary has one home. */
  services:            string | null;
  /** The EARLIEST scheduled task's date — "next" along the request's own
   *  timeline, so a completed request still shows when it was due. */
  nextAppointmentDate: string | null;
  /** That same task's time ("14:00:00"), when one is set. */
  nextAppointmentTime: string | null;
  /** That same task's site address, so Location describes the appointment
   *  beside it. */
  location:            string | null;
  /** How many live Service Tasks the request has. */
  serviceCount:        number;
}

/** One service category — the "All categories" filter options and the Services
 *  column's chip rendering. Served by the API (`/categories`) so the
 *  vocabulary is not duplicated in the client; becomes AI_ServiceDefinition-
 *  backed when the configuration switchboard (design §5) lands. */
export interface ServiceCategory {
  code: string;
  name: string;
  /** The shared pill tone class the chip renders in. */
  tone: string;
}

/** One request in full — the grid row plus the description. */
export interface ClientRequestDetail extends ClientRequestRow {
  description: string | null;
  /** The date the client actually rang, as `YYYY-MM-DD` (a calendar day, so it
   *  carries no time and is never timezone-shifted).  Distinct from
   *  `createdAt`, which is when the ROW was made. */
  calledOn:    string | null;
  heardAbout:  string | null;
}

/** The create / update payload.  Status and requestNo are absent by design:
 *  the number is permanent and status moves only through its own transitions
 *  (design §4, §9.1). */
export interface ClientRequestInput {
  clientId:    string | null;
  contactId:   string | null;
  projectId:   string | null;
  priority:    string | null;
  /** The chosen Priority row's Oid — what the server saves and checks. */
  priorityId:  string | null;
  quoteNo:     string | null;
  /** `YYYY-MM-DD`, sent as a plain date string — never an instant. */
  calledOn:    string | null;
  heardAbout:  string | null;
  description: string | null;
  ownerId:     string | null;
}

/** One option in a Client / Contact / Project picker. */
export interface LookupItem {
  id:        string;
  label:     string;
  secondary: string | null;
}

// ── Service Tasks ───────────────────────────────────────────────────────────

/** "This field only applies when `fieldId` equals `value`". */
export interface FieldCondition { fieldId: string; value: string }

/** One dynamic field on a Service Task, as the definition declares it. */
export interface ServiceFieldDef {
  id:          string;
  caption:     string;
  /** 'sel' (dropdown), 'msel' (several picks, stored joined by "; ") or 'txt'. */
  type:        string;
  required:    boolean;
  options:     string[];
  placeholder: string | null;
  /** ALL conditions must hold for the field to apply; empty = always. */
  when:        FieldCondition[];
}

/** The Turnaround field's id — a new service inherits the request's Priority
 *  into it. Like Sample collection, Proof required, Fulfilment method and
 *  Document type, its options are the live rows of a Settings form and the
 *  server stores the chosen row's Oid; the form works on the NAME. */
export const TURNAROUND_FIELD = 'tat';

/** One conditional Job-rule row (design §7.3), evaluated first-match-wins by
 *  `resolveJobRule`. `whenField` null makes the row the fallback; the literal
 *  `"type"` keys off the service type rather than a dynamic field answer. */
export interface JobRuleDef {
  text:       string;
  whenField:  string | null;
  whenValues: string[] | null;
}

/** One category from the Service Definition switchboard (design §5). The client
 *  renders the entire work form from this and holds no category logic of its
 *  own — which is what makes a new category a config row, not a release. */
export interface ServiceCategoryDef {
  code:            string;
  name:            string;
  tone:            string;
  icon:            string;
  workLabel:       string;
  note:            string;
  /** 'required' | 'optional' | 'by-field'. */
  routeRule:       string;
  routeWhenField:  string | null;
  routeWhenValues: string[];
  /** For a by-field rule: is a route required while the deciding field is still
   *  unanswered? Delivery says yes, Bottle Order says no — so it is per-category
   *  configuration, not one hardcoded default. */
  routeWhenUnset:  boolean;
  /** What the technician submits when the work is done — the Survey Submission,
   *  the eCOC, the Pickup Receipt. Shown on the task row beside the Job rule. */
  completionRecord: string;
  /** Whether a laboratory Job follows, as CONFIGURATION rather than a per-category
   *  branch in code — see `resolveJobRule`. */
  jobRules:        JobRuleDef[];
  types:           string[];
  fields:          ServiceFieldDef[];
  byType:          Record<string, ServiceFieldDef[]>;
  /** The Service Task Report this category produces (§8). */
  report:          ServiceReportDef | null;
  /** A service TYPE whose report differs from its category's default. */
  reportByType:    Record<string, ServiceReportDef> | null;
}

/** How a category's Service Task Report behaves (design §8). A service's report
 *  is a SEPARATE artefact from the laboratory's analytical report — where
 *  samples are collected the two run in parallel:
 *
 *      Client Request → Service Task ─┬→ Service Task Report
 *                                     └→ eCOC → Laboratory Job → Analytical Report
 */
export interface ServiceReportDef {
  /** Whether this service produces one at all. */
  required:         boolean;
  /** The choices; the first is the default. */
  templates:        string[];
  /** The event that raises it. */
  generatedOn:      string;
  approvalRequired: boolean;
  clientVisible:    boolean;
  /** What is carried into the document. */
  includes:         string[];
  /** Why this service is configured this way — shown to the user. */
  note:             string;
}

/** The report configuration in force for a task (§8) — the category's, unless
 *  the chosen service type overrides it. Mirrors
 *  `ServiceDefinitions.ResolveReport`. */
export function resolveReport(
  cat: ServiceCategoryDef | undefined, serviceType: string | null,
): ServiceReportDef | null {
  if (!cat) return null;
  if (serviceType && cat.reportByType?.[serviceType]) return cat.reportByType[serviceType];
  return cat.report ?? null;
}

/** One Service Task under a request. */
export interface ServiceTaskRow {
  id:             string;
  requestId:      string;
  serviceNo:      string | null;
  categoryCode:   string | null;
  serviceType:    string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  /** "HH:mm:ss" wall-clock, or null when only the day is agreed. */
  scheduledTime:  string | null;
  assignedToId:   string | null;
  assignedToName: string | null;
  routeId:        string | null;
  /** The dispatcher decided a route is needed, as distinct from not having
   *  looked yet — both leave routeId null (v46 `routeLater`). */
  routeLater:     boolean;
  routeName:      string | null;
  instructions:   string | null;
  /** Which Service Task Report template this service uses (§8); null keeps
   *  it on the category default rather than pinning a copy of it. */
  reportTemplate: string | null;
  /** The report's state (§8). Version counts UP and is never reset —
   *  regenerating supersedes v1 with v2. Null = nothing raised yet. */
  reportVersion:     number | null;
  reportGeneratedAt: string | null;
  reportApprovedAt:  string | null;
  /** Resolved name, so the report table shows a person not an Oid. */
  reportApprovedByName: string | null;
  isDraft:        boolean;
  jobNo:          string | null;
  createdAt:      string;
  /** Dynamic answers keyed by field id. */
  fields:         Record<string, string | null>;
}

/** Create / update payload for a Service Task. */
export interface ServiceTaskInput {
  categoryCode:   string | null;
  serviceType:    string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  scheduledTime:  string | null;
  assignedToId:   string | null;
  routeId:        string | null;
  /** The dispatcher decided a route is needed, as distinct from not having
   *  looked yet — both leave routeId null (v46 `routeLater`). */
  routeLater:     boolean;
  routeName:      string | null;
  instructions:   string | null;
  /** Which Service Task Report template this service uses (§8); null keeps
   *  it on the category default rather than pinning a copy of it. */
  reportTemplate: string | null;
  fields:         Record<string, string | null>;
  /** Field ids staged for clearing by a type change (§6.1) — sent only at
   *  save, so a Discard never records a change that did not happen. */
  clearedFields:  string[];
}

/** Raise ("generate") or approve the service's Service Task Report (§8).
 *  Returns the resulting version. The report belongs to a SAVED service — its
 *  identity is the Service ID — so this is only callable once the service is
 *  committed. */
export async function serviceReportAction(
  serviceId: string, action: 'generate' | 'approve',
): Promise<number> {
  const res = await api.post<{ success: boolean; data: { version: number } }>(
    `/api/v1/services/${serviceId}/report/${action}`);
  return res.data.data?.version ?? 0;
}

/** Release the request for scheduling — the §10.3 gate. Returns the new status.
 *  The SERVER re-checks the whole gate; when it refuses, the rejection carries a
 *  `details` array of blocker lines, which is what the caller should show. It is
 *  attached to the thrown error so the caller does not have to unpick an axios
 *  response shape. */
export async function releaseRequest(id: string): Promise<string> {
  try {
    const res = await api.post<{ success: boolean; data: { status: string } }>(
      `${BASE}/${id}/release`);
    return res.data.data?.status ?? 'Submitted';
  } catch (e) {
    const body = (e as { response?: { data?: { error?: { message?: string; details?: string[] } } } })
      .response?.data?.error;
    const err = new Error(body?.message ?? 'Could not release the request.');
    if (Array.isArray(body?.details)) (err as Error & { details?: string[] }).details = body.details;
    throw err;
  }
}

/** One row of the Service Allocation board (design §12) — every live, SAVED
 *  service across ALL requests. Drafts are excluded server-side: work nobody
 *  committed to must not appear on a dispatch board. */
export interface ServiceAllocationRow {
  id:             string;
  requestId:      string;
  serviceNo:      string | null;
  requestNo:      string | null;
  categoryCode:   string | null;
  serviceType:    string | null;
  clientId:       string | null;
  clientName:     string | null;
  projectCode:    string | null;
  /** The parent request's priority (its agreed TURNAROUND) — drives the
   *  board's urgency reminder. */
  priority:       string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  scheduledTime:  string | null;
  assignedToId:   string | null;
  assignedToName: string | null;
  routeId:        string | null;
  jobNo:          string | null;
  createdAt:      string;
}

/** Readiness, DERIVED — never stored. A service is only dispatchable once it
 *  has a date, someone to drive it, and a route; the label says which of those
 *  is missing rather than just "not ready". Mirrors prototype v43 taskStatus(). */
export function allocationStatus(r: ServiceAllocationRow):
  { label: string; tone: string } {
  if (!r.scheduledDate)  return { label: 'Unscheduled',    tone: 'warn' };
  if (!r.assignedToId)   return { label: 'Unassigned',     tone: 'warn' };
  if (!r.routeId)        return { label: 'Scheduled',      tone: 'info' };
  return                        { label: 'Dispatch ready', tone: 'ok'   };
}

/**
 * The ONE thing this service still needs, as a nudge for the dispatcher —
 * DERIVED, never stored.  Prototype v46 `taskTip()`.
 *
 * <p>Ordered on purpose: it answers "what do I do about this row NEXT", so the
 * first unmet rule wins and the dispatcher is never handed a list to triage.
 * That is what makes it different from the Status column beside it — Status
 * says how far along the row is, this says what to do about it.</p>
 *
 * <p>v46 has a seventh rule ("Container kit not chosen yet"). It is deliberately
 * absent here: in our Service Definitions the kit field is `required`, and this
 * board shows only SAVED services, which cannot be missing a required field.
 * The rule could never fire, so showing it would imply a state we do not have.</p>
 */
export function serviceReminder(r: ServiceAllocationRow):
  { icon: string; tone: string; tip: string } {
  if (!r.scheduledDate)
    return { icon: 'ti-calendar-x', tone: 'alert',
             tip: 'Pick a date before this can be routed.' };
  if (!r.assignedToId)
    return { icon: 'ti-user-question', tone: 'warn',
             tip: 'No driver yet — assign one to release it.' };
  // "Rush" no longer exists (prototype v68). The commitment the lab made on
  // the call is a TURNAROUND, and the two that cannot wait for tomorrow are a
  // mobile-lab visit and a same-day promise — so those are what the board
  // flames, not a generic urgency word.
  const tat = (r.priority ?? '').toLowerCase();
  if (tat === 'mobile lab' || tat === 'same day')
    return { icon: 'ti-flame', tone: 'alert',
             tip: `${r.priority} turnaround — schedule this first.` };
  if (isToday(r.scheduledDate))
    return { icon: 'ti-alarm', tone: 'warn',
             tip: 'Today — confirm the site contact before arrival.' };
  if (!r.routeId)
    return { icon: 'ti-route', tone: 'info',
             tip: 'Not on a route — add it in Route planning.' };
  return { icon: 'ti-circle-check', tone: 'ok', tip: 'Ready to dispatch.' };
}

/** Is this wire date the LOCAL calendar day?  Compared as `YYYY-MM-DD` text
 *  built from local parts — never by constructing a Date from the wire value,
 *  which would drag a calendar day across midnight for anyone west of the
 *  server and make "today" wrong for a whole timezone. */
function isToday(wire: string | null): boolean {
  if (!wire) return false;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
              + `-${String(d.getDate()).padStart(2, '0')}`;
  return wire.slice(0, 10) === today;
}

/** One Draft request whose saved services the dispatch screens hold back. */
export type HeldBackRequestRow = { requestNo: string; services: number };

/**
 * The services the dispatch screens are NOT showing, because their request is
 * still a Draft (release gate). `silentLoading` because it only annotates a
 * screen that is already loading its own data.
 */
export async function fetchHeldBackRequests(): Promise<HeldBackRequestRow[]> {
  const res = await api.get<{ success: boolean; data: HeldBackRequestRow[] }>(
    '/api/v1/services/held-back', { silentLoading: true } as never);
  return res.data.data ?? [];
}

export async function fetchServiceAllocation(): Promise<ServiceAllocationRow[]> {
  const res = await api.get<{ success: boolean; data: ServiceAllocationRow[] }>(
    '/api/v1/service-allocation');
  return res.data.data ?? [];
}

/** Move one service to a date and/or a driver. Narrow on purpose — the board
 *  must never be able to change a category, address or field answer. */
export async function allocateService(
  serviceId: string, scheduledDate: string | null, assignedToId: string | null,
): Promise<void> {
  await api.patch(`/api/v1/services/${serviceId}/allocation`,
    { scheduledDate, assignedToId });
}

/** Records what the FIELD did — arrival on site, then completion (Service
 *  Tracking; prototype v68 markArrived / markDone) — and returns the stamp now
 *  on the service. The server enforces the order and refuses a second record;
 *  its message says which rule stopped it. */
export async function recordServiceProgress(
  serviceId: string, step: 'arrived' | 'completed',
): Promise<string> {
  const res = await api.post<{ success: boolean; data: { at: string } }>(
    `/api/v1/services/${serviceId}/progress/${step}`);
  return res.data.data.at;
}

/** One row of SERVICE TRACKING — the historical view of a released service.
 *  Carries the stamps a STAGE is derived from, plus the parent request's
 *  `requestedAt`, which is the lifecycle's first step and lives nowhere on the
 *  service itself. */
export interface ServiceTrackingRow {
  id:             string;
  requestId:      string;
  serviceNo:      string | null;
  requestNo:      string | null;
  categoryCode:   string | null;
  serviceType:    string | null;
  clientId:       string | null;
  clientName:     string | null;
  projectCode:    string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  scheduledTime:  string | null;
  assignedToId:   string | null;
  assignedToName: string | null;
  routeId:        string | null;
  /** The route's human code (RT260905-01) — what the Route column shows. */
  routeCode:      string | null;
  routeName:      string | null;
  jobNo:          string | null;
  arrivedAt:      string | null;
  completedAt:    string | null;
  requestedAt:    string;
}

/** The lifecycle a service walks, in order. The rail renders all five; the
 *  stage index says how far along it is. */
export const SERVICE_STAGES = ['Requested', 'Scheduled', 'Dispatched', 'On site', 'Completed'] as const;

/**
 * What stage a service is in — DERIVED from what the field actually did, never
 * stored and never typed (prototype v46 `svcStage`). One rule, three readers:
 * the Stage column, the stage filter and the detail rail, so they cannot
 * disagree about where a service has got to.
 *
 * Read newest-fact-first: a completed service is Completed whatever else is set.
 *
 * v46 tones a Scheduled service teal; this theme has no teal token, so the five
 * stages are mapped onto EXISTING pill tokens rather than introducing a palette
 * for one column (the same call made for the allocation board's alert tone).
 */
export function serviceStage(r: {
  scheduledDate: string | null; routeId: string | null;
  arrivedAt: string | null; completedAt: string | null;
}): { label: string; tone: string; index: number } {
  if (r.completedAt)   return { label: 'Completed',   tone: 'ok',      index: 4 };
  if (r.arrivedAt)     return { label: 'On site',     tone: 'orange',  index: 3 };
  if (r.routeId)       return { label: 'Dispatched',  tone: 'violet',  index: 2 };
  if (r.scheduledDate) return { label: 'Scheduled',   tone: 'info',    index: 1 };
  return                      { label: 'Unscheduled', tone: 'neutral', index: 0 };
}

export async function fetchServiceTracking(): Promise<ServiceTrackingRow[]> {
  const res = await api.get<{ success: boolean; data: ServiceTrackingRow[] }>(
    '/api/v1/service-tracking');
  return res.data.data ?? [];
}

// ── Route Tracking (design §12; prototype v46) ────────────────────────────

/** A scheduled service waiting to be put on a route. */
export interface RoutePendingRow {
  id:             string;
  requestId:      string;
  serviceNo:      string | null;
  categoryCode:   string | null;
  serviceType:    string | null;
  clientName:     string | null;
  projectCode:    string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  scheduledTime:  string | null;
  assignedToId:   string | null;
  assignedToName: string | null;
}

/** A dated RUN of a route. Note there is no `status` — see `routeStatus`. */
export interface RouteRow {
  id:             string;
  routeNo:        string | null;
  routeName:      string | null;
  routeDate:      string | null;
  driverId:       string | null;
  driverName:     string | null;
  vehicleCode:    string | null;
  /** The dispatcher's note on the run (v68 Remark). */
  remark:         string | null;
  publishedAt:    string | null;
  stopCount:      number;
  arrivedCount:   number;
  completedCount: number;
  createdAt:      string;
}

/** One stop — a Service Task in its visit position. */
export interface RouteStopRow {
  id:             string;
  requestId:      string;
  /** The request's RR number (the Stops list's RequestID column). */
  requestNo:      string | null;
  serviceNo:      string | null;
  routeId:        string;
  routeNo:        string | null;
  routeName:      string | null;
  routeDate:      string | null;
  routeSeq:       number | null;
  /** The ROUTE's remark — the Stops list's Remark column (v68). */
  routeRemark:    string | null;
  categoryCode:   string | null;
  serviceType:    string | null;
  clientName:     string | null;
  projectCode:    string | null;
  siteAddress:    string | null;
  scheduledDate:  string | null;
  scheduledTime:  string | null;
  assignedToId:   string | null;
  assignedToName: string | null;
  arrivedAt:      string | null;
  completedAt:    string | null;
  jobNo:          string | null;
}

/**
 * A route's status — DERIVED from its stops, never stored (v46: "Status is
 * derived from the stops — a route is complete when its last stop is").
 *
 * Storing it would let the header claim a route is finished while a stop says
 * otherwise. Read newest-fact-first, same shape as `serviceStage`.
 */
export function routeStatus(r: {
  publishedAt: string | null; stopCount: number;
  arrivedCount: number; completedCount: number;
}): { label: string; tone: string } {
  if (r.stopCount > 0 && r.completedCount >= r.stopCount)
    return { label: 'Completed',   tone: 'ok' };
  if (r.arrivedCount > 0)  return { label: 'In Progress', tone: 'info' };
  if (r.publishedAt)       return { label: 'Published',   tone: 'orange' };
  return                          { label: 'Draft',       tone: 'neutral' };
}

/**
 * What still stands between a route and the field, named rather than counted:
 * "cannot publish" leaves a dispatcher hunting for which of three things is
 * missing. Mirrors the server's gate, which stays authoritative (§14b.5).
 */
export function routePublishBlockers(r: {
  driverId: string | null; vehicleCode: string | null; stopCount: number;
}): string[] {
  const b: string[] = [];
  if (!r.driverId)    b.push('no driver');
  if (!r.vehicleCode) b.push('no vehicle');
  if (!r.stopCount)   b.push('no stops');
  return b;
}

/** Is a pending service ready to be routed, or does it still need a driver? */
export function pendingReadiness(r: { assignedToId: string | null }):
  { label: string; tone: string } {
  return r.assignedToId
    ? { label: 'Ready to route', tone: 'ok' }
    : { label: 'Needs a driver', tone: 'warn' };
}

export async function fetchRoutePending(): Promise<RoutePendingRow[]> {
  const res = await api.get<{ success: boolean; data: RoutePendingRow[] }>('/api/v1/route-pending');
  return res.data.data ?? [];
}

export async function fetchRouteRuns(): Promise<RouteRow[]> {
  // route-runs, not routes: /api/v1/routes is the Settings → Utility → Routes
  // master (see RouteController's header — the two collided and both 500'd).
  const res = await api.get<{ success: boolean; data: RouteRow[] }>('/api/v1/route-runs');
  return res.data.data ?? [];
}

export async function fetchRouteStops(routeId?: string): Promise<RouteStopRow[]> {
  const res = await api.get<{ success: boolean; data: RouteStopRow[] }>(
    '/api/v1/route-stops', { params: routeId ? { routeId } : undefined });
  return res.data.data ?? [];
}

export async function createRoute(input: {
  routeName: string | null; routeDate: string | null;
  driverId: string | null; vehicleCode: string | null; serviceIds: string[];
}): Promise<{ id: string; routeNo: string; stopsAttached: number }> {
  const res = await api.post<{ success: boolean; data: { id: string; routeNo: string; stopsAttached: number } }>(
    '/api/v1/route-runs', input);
  return res.data.data;
}

/** The vehicles a route can be driven in (prototype v68). Served by the API so
 *  the dropdown and the server validator read one list. */
export async function fetchRouteVehicles(): Promise<string[]> {
  const res = await api.get<{ success: boolean; data: string[] }>('/api/v1/route-vehicles');
  return res.data.data ?? [];
}

export async function updateRoute(id: string, input: {
  routeName: string | null; routeDate: string | null;
  driverId: string | null; vehicleCode: string | null; publish: boolean;
  remark: string | null;
}): Promise<void> {
  await api.put(`/api/v1/route-runs/${id}`, input);
}

/** Move a stop, or take it off the route. */
export async function moveRouteStop(
  serviceId: string, direction: 'up' | 'down' | 'remove',
): Promise<boolean> {
  const res = await api.patch<{ success: boolean; data?: { moved: boolean } }>(
    `/api/v1/route-stops/${serviceId}`, { direction });
  return res.data.data?.moved ?? true;
}

export async function deleteRoute(id: string): Promise<void> {
  await api.delete(`/api/v1/route-runs/${id}`);
}

export async function fetchServiceDefinitions(): Promise<ServiceCategoryDef[]> {
  const res = await api.get<{ success: boolean; data: ServiceCategoryDef[] }>(
    '/api/v1/service-definitions');
  return res.data.data ?? [];
}

export async function fetchTechnicians(): Promise<{ id: string; label: string }[]> {
  const res = await api.get<{ success: boolean; data: { id: string; label: string }[] }>(
    '/api/v1/service-lookups/technicians');
  return res.data.data ?? [];
}

export async function fetchServiceTasks(requestId: string): Promise<ServiceTaskRow[]> {
  const res = await api.get<{ success: boolean; data: ServiceTaskRow[] }>(
    `${BASE}/${requestId}/services`);
  return res.data.data ?? [];
}

/** Adds a task and returns its minted, permanent Service ID. */
export async function createServiceTask(
  requestId: string, input: ServiceTaskInput,
): Promise<{ id: string; serviceNo: string }> {
  const res = await api.post<{ success: boolean; data: { id: string; serviceNo: string } }>(
    `${BASE}/${requestId}/services`, input);
  return res.data.data;
}

export async function updateServiceTask(serviceId: string, input: ServiceTaskInput): Promise<void> {
  await api.put(`/api/v1/services/${serviceId}`, input);
}

export async function deleteServiceTask(serviceId: string): Promise<void> {
  await api.delete(`/api/v1/services/${serviceId}`);
}

// ── The field resolver, client-side ─────────────────────────────────────────
// The SAME three-level rule the server runs (design §6): category fields + the
// service type's extra fields, filtered by each field's conditions. The form,
// the client validator and the orphan check all call THIS one function — any
// consumer that builds its own list is a defect, because the moment two lists
// disagree the audit stops matching what the user saw.

export function resolveFields(
  cat: ServiceCategoryDef | undefined,
  serviceType: string | null,
  values: Record<string, string | null>,
): ServiceFieldDef[] {
  if (!cat) return [];
  const all = [...cat.fields, ...(serviceType ? cat.byType[serviceType] ?? [] : [])];
  return all.filter(f => f.when.length === 0
    || f.when.every(c => (values[c.fieldId] ?? '') === c.value));
}

/** Every field a category could EVER show, across all its types and conditions
 *  — the candidate set when looking for orphans after a type change (§6.1). */
export function allFieldsOf(cat: ServiceCategoryDef | undefined): ServiceFieldDef[] {
  if (!cat) return [];
  const seen = new Map<string, ServiceFieldDef>();
  for (const f of [...cat.fields, ...Object.values(cat.byType).flat()]) {
    if (!seen.has(f.id)) seen.set(f.id, f);
  }
  return [...seen.values()];
}

/** What the Job rule resolves to for this task (design §7.3) — the text on the
 *  task row and in the work form. Mirrors `ServiceDefinitions.ResolveJobRule`;
 *  generic and first-match-wins, so no category is branched on in code. An
 *  unanswered deciding field falls through to the unconditional entry, which is
 *  what makes "Set Sample collection" appear rather than a guess. */
export function resolveJobRule(
  cat: ServiceCategoryDef | undefined,
  serviceType: string | null,
  values: Record<string, string | null>,
): string {
  if (!cat) return '';
  for (const rule of cat.jobRules ?? []) {
    if (rule.whenField === null) return rule.text;          // the fallback
    const actual = rule.whenField === 'type' ? serviceType : values[rule.whenField];
    if (actual && rule.whenValues?.includes(actual)) return rule.text;
  }
  return '';
}

/** Whether this task will need a route (§7.1) — SHOWN, not enforced at intake.
 *  A route is built by dispatch, not on the client call, so requiring one at
 *  save would block release on a decision the person taking the call is not
 *  making. This drives the "Required before dispatch" label; the requirement is
 *  enforced before a route is PUBLISHED (prototype v43 taskNeed()/routeBlockers();
 *  v21 validated it at save).
 *  FAIL-SAFE: a by-field rule whose deciding answer is not yet given resolves to
 *  REQUIRED, so an incomplete record errs toward being routed rather than
 *  silently unrouted. */
export function routeRequired(
  cat: ServiceCategoryDef | undefined, values: Record<string, string | null>,
): boolean {
  if (!cat) return false;
  if (cat.routeRule === 'required') return true;
  if (cat.routeRule === 'optional') return false;
  if (!cat.routeWhenField) return cat.routeWhenUnset;
  const v = values[cat.routeWhenField];
  // Unanswered defers to the category rather than assuming "required": a Bottle
  // Order only joins a route once someone says the lab carries it, while a
  // Delivery travels unless a courier is named.
  return v ? cat.routeWhenValues.some(w => answerMatches(w, v)) : cat.routeWhenUnset;
}

/** Does an answer MEAN the configured value? The deciding answers now come from
 *  Settings (Ship via / Fulfilment Method), so an exact match would let a
 *  re-capitalisation or a qualified rename silently switch a route requirement
 *  off. Mirrors ServiceDefinitions.AnswerMatches on the server — the server is
 *  the authority; this keeps the screen from disagreeing with it. */
function answerMatches(configured: string, answer: string): boolean {
  const squash = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const a = squash(configured), b = squash(answer);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

const BASE = '/api/v1/client-requests';

export async function fetchRequest(id: string): Promise<ClientRequestDetail | null> {
  const res = await api.get<{ success: boolean; data: ClientRequestDetail }>(`${BASE}/${id}`);
  return res.data.success ? res.data.data : null;
}

/** Registers a request and returns the minted, permanent RequestNo. */
export async function createRequest(input: ClientRequestInput): Promise<{ id: string; requestNo: string }> {
  const res = await api.post<{ success: boolean; data: { id: string; requestNo: string } }>(BASE, input);
  return res.data.data;
}

export async function updateRequest(id: string, input: ClientRequestInput): Promise<void> {
  await api.put(`${BASE}/${id}`, input);
}

/** The live Priority rows, in their Settings order. */
export async function fetchPriorities(): Promise<PriorityOption[]> {
  const res = await api.get<{ success: boolean; data: PriorityOption[] }>(`${BASE}/priorities`);
  return res.data.data ?? [];
}

export async function fetchHeardOptions(): Promise<string[]> {
  const res = await api.get<{ success: boolean; data: string[] }>(`${BASE}/heard-options`);
  return res.data.data ?? HEARD_FALLBACK;
}

export async function fetchCategories(): Promise<ServiceCategory[]> {
  const res = await api.get<{ success: boolean; data: ServiceCategory[] }>(`${BASE}/categories`);
  return res.data.data ?? [];
}

// ── Quick-add (the inline ＋ beside a picker) ───────────────────────────────
// All three reuse the EXISTING Clients master-data endpoints rather than adding
// a second create path for the same tables (standing rule 8). The new record is
// therefore identical to one created from the Clients form, and is audited by
// that module exactly as it would be there.
//
// CLIENT (2026-09-16, prototype v68): `dbo.Customer` previously had no create
// endpoint anywhere, which is why this file used to say a client could only be
// made in the Clients form. Rather than mint one from here — duplicating that
// module's validation and ownership — `POST /api/v1/clients` was added TO the
// Clients module, and this calls it.

/** Creates a contact under the given client and returns its new Oid.
 *  `FirstName` and `Email` are the API's required pair. */
export async function createContact(
  clientId: string, input: { firstName: string; lastName?: string; email: string; officePhone?: string; position?: string },
): Promise<string> {
  const res = await api.post<{ success: boolean; data: { oid: string } }>(
    `/api/v1/clients/${clientId}/contacts`, {
      FirstName:   input.firstName,
      LastName:    input.lastName ?? null,
      Email:       input.email,
      OfficePhone: input.officePhone ?? null,
      Position:    input.position ?? null,
      Prospect:    false,
      // Type 0 = Report Contact, the default a request contact should be.
      Type:        0,
      SampleReceiptNotification: false,
      ReportDelivery:            false,
    });
  return res.data.data.oid;
}

/** Creates a project under the given client and returns its new Oid.
 *  `ProjectId` (the client-facing code) is the API's required field. */
export async function createProject(
  clientId: string, input: { projectId: string; projectName?: string; projectLocation?: string },
): Promise<string> {
  const res = await api.post<{ success: boolean; data: { oid: string } }>(
    `/api/v1/clients/${clientId}/projects`, {
      ProjectId:       input.projectId,
      ProjectName:     input.projectName ?? null,
      ProjectLocation: input.projectLocation ?? null,
      Comment:         null,
    });
  return res.data.data.oid;
}

/**
 * One payment term from Settings → Accounting.
 *
 * <p>`dbo.PaymentTerms.Terms` is a count of DAYS — the column holds `30`, not
 * the words "Net 30" — so the option is labelled from that number rather than
 * dressed up. The table is empty on a fresh database, which is why the dropdown
 * can legitimately show nothing but "— select —" until the lab adds terms.</p>
 */
export interface PaymentTermOption {
  id:   string;
  days: number;
}

/** The lab's payment terms, from the EXISTING Accounting endpoint (rule 8) —
 *  never a second query with its own idea of the list. */
export async function fetchPaymentTerms(): Promise<PaymentTermOption[]> {
  const res = await api.get<{ success: boolean; data: { id: string; terms: number }[] }>(
    '/api/v1/accounting/payment-terms');
  return (res.data.data ?? []).map(t => ({ id: t.id, days: t.terms }));
}

/**
 * Creates a client and returns its new Oid.
 *
 * <p>`accountNumber` is NOT a column on the client: the API files it as a
 * `dbo.ClientAccountNumber` row underneath it. `termsId` is the Oid of a
 * `dbo.PaymentTerms` row, which `dbo.Customer.Terms` points at.</p>
 */
export async function createClient(input: {
  customerName:   string;
  accountNumber?: string;
  officePhone?:   string;
  email?:         string;
  termsId?:       string;
}): Promise<string> {
  const res = await api.post<{ success: boolean; data: { oid: string } }>(
    '/api/v1/clients', {
      CustomerName:  input.customerName,
      AccountNumber: input.accountNumber ?? null,
      OfficePhone:   input.officePhone ?? null,
      Email:         input.email ?? null,
      TermsId:       input.termsId ?? null,
    });
  return res.data.data.oid;
}

export async function fetchClients(): Promise<LookupItem[]> {
  const res = await api.get<{ success: boolean; data: LookupItem[] }>(`${BASE}/lookups/clients`);
  return res.data.data ?? [];
}

/** The lab users a request can be OWNED by (prototype v68).  Same roster the
 *  technician picker uses — in a small laboratory the person who takes the call
 *  also drives to site — but requested through an OWNER-named endpoint, so the
 *  two can diverge later without a caller changing. */
export async function fetchOwners(): Promise<LookupItem[]> {
  const res = await api.get<{ success: boolean; data: LookupItem[] }>(`${BASE}/lookups/owners`);
  return res.data.data ?? [];
}

/** The contacts ON FILE for one client — the §10.1 narrowing.  An empty array
 *  is a real answer, not a failure. */
export async function fetchContacts(clientId: string): Promise<LookupItem[]> {
  const res = await api.get<{ success: boolean; data: LookupItem[] }>(
    `${BASE}/lookups/contacts`, { params: { clientId } });
  return res.data.data ?? [];
}

export async function fetchProjects(clientId: string): Promise<LookupItem[]> {
  const res = await api.get<{ success: boolean; data: LookupItem[] }>(
    `${BASE}/lookups/projects`, { params: { clientId } });
  return res.data.data ?? [];
}

/** One row of the shared audit trail, as `dbo.AuditData` records it. */
export interface AuditTrailRow {
  oid:             string;
  createdDate?:    string | null;
  createdBy?:      string | null;
  operationType?:  string | null;
  propertyName?:   string | null;
  oldValue?:       string | null;
  newValue?:       string | null;
  comment?:        string | null;
}

/** Read one record's audit history from the SHARED endpoint (§14b.2) — the same
 *  rows the Audit Trail Log and the toolbar's audit popup show. Never a second
 *  query with its own idea of what happened (standing rule 8). */
export async function fetchAuditTrail(
  formName: string, sourceId: string,
): Promise<AuditTrailRow[]> {
  const res = await api.get<{ success: boolean; data: AuditTrailRow[] }>(
    '/api/v1/audit/trail', { params: { formName, sourceId } });
  return res.data.data ?? [];
}
